import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSpotlight() {
  const { getToken } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ user_name: "", dish_name: "", description: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const getHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/spotlight`, { headers: getHeaders() });
      setPosts(data);
    } catch { toast.error("Failed to load spotlight posts"); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => {
    setEditingPost(null);
    setForm({ user_name: "", dish_name: "", description: "" });
    setImageFile(null);
    setImagePreview("");
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({ user_name: post.user_name, dish_name: post.dish_name, description: post.description || "" });
    setImageFile(null);
    setImagePreview(post.image?.startsWith("http") ? post.image : `${API}${post.image}`);
    setDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.user_name || !form.dish_name) return toast.error("Name and dish name are required");
    if (!editingPost && !imageFile) return toast.error("Image is required for new posts");
    setSaving(true);
    try {
      if (editingPost) {
        let imageUrl = editingPost.image;
        if (imageFile) {
          const fd = new FormData();
          fd.append("image", imageFile);
          const { data: upload } = await axios.post(`${API}/api/admin/upload-image`, fd, { headers: getHeaders() });
          imageUrl = upload.url;
        }
        await axios.put(`${API}/api/admin/spotlight/${editingPost.id}`, {
          ...form, image: imageUrl
        }, { headers: getHeaders() });
        toast.success("Spotlight post updated");
      } else {
        const fd = new FormData();
        fd.append("user_name", form.user_name);
        fd.append("dish_name", form.dish_name);
        fd.append("description", form.description);
        fd.append("image", imageFile);
        await axios.post(`${API}/api/admin/spotlight`, fd, { headers: getHeaders() });
        toast.success("Spotlight post created");
      }
      setDialogOpen(false);
      fetchPosts();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/api/admin/spotlight/${id}/approve`, {}, { headers: getHeaders() });
      toast.success("Post approved");
      fetchPosts();
    } catch { toast.error("Failed to approve"); }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/api/admin/spotlight/${id}/reject`, {}, { headers: getHeaders() });
      toast.success("Post rejected");
      fetchPosts();
    } catch { toast.error("Failed to reject"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this spotlight post?")) return;
    try {
      await axios.delete(`${API}/api/admin/spotlight/${id}`, { headers: getHeaders() });
      toast.success("Post deleted");
      fetchPosts();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter === "all" ? posts : filter === "approved" ? posts.filter(p => p.approved) : posts.filter(p => !p.approved);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-praroha-sage border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-spotlight">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-praroha-forest">Customer Spotlight</h1>
          <p className="text-sm text-muted-foreground mt-1">{posts.length} total, {posts.filter(p => !p.approved).length} pending approval</p>
        </div>
        <Button onClick={openNew} className="bg-praroha-sage hover:bg-praroha-forest" data-testid="add-spotlight-btn">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      <div className="flex gap-2">
        {["all", "approved", "pending"].map(f => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm"
            onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={filter === f ? "bg-praroha-sage hover:bg-praroha-forest" : ""}>
            {f.charAt(0).toUpperCase() + f.slice(1)} {f === "pending" ? `(${posts.filter(p => !p.approved).length})` : ""}
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No spotlight posts found</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(post => (
            <Card key={post.id} className={`overflow-hidden ${!post.approved ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid={`spotlight-card-${post.id}`}>
              <div className="relative aspect-[4/3]">
                <img
                  src={post.image?.startsWith("http") ? post.image : `${API}${post.image}`}
                  alt={post.dish_name}
                  className="w-full h-full object-cover"
                />
                <Badge className={`absolute top-2 right-2 ${post.approved ? 'bg-green-600' : 'bg-amber-500'}`}>
                  {post.approved ? "Approved" : "Pending"}
                </Badge>
              </div>
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-praroha-forest">{post.dish_name}</h3>
                <p className="text-sm text-muted-foreground">by {post.user_name}</p>
                {post.description && <p className="text-sm line-clamp-2">{post.description}</p>}
                <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString()}</p>
                <div className="flex gap-2 pt-2">
                  {!post.approved && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => handleApprove(post.id)} data-testid={`approve-${post.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {post.approved && (
                    <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      onClick={() => handleReject(post.id)} data-testid={`reject-${post.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Unapprove
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(post)} data-testid={`edit-${post.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50"
                    onClick={() => handleDelete(post.id)} data-testid={`delete-${post.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Spotlight Post" : "New Spotlight Post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Customer Name *</Label><Input value={form.user_name} onChange={e => setForm(f => ({...f, user_name: e.target.value}))} data-testid="spotlight-name-input" /></div>
            <div><Label>Dish Name *</Label><Input value={form.dish_name} onChange={e => setForm(f => ({...f, dish_name: e.target.value}))} data-testid="spotlight-dish-input" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} data-testid="spotlight-desc-input" /></div>
            <div>
              <Label>Image {!editingPost && "*"}</Label>
              <div className="mt-1">
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-md" />
                    <Button size="sm" variant="outline" className="absolute bottom-2 right-2 bg-white/90"
                      onClick={() => document.getElementById("spotlight-img-input").click()}>
                      <Upload className="w-3.5 h-3.5 mr-1" /> Change
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => document.getElementById("spotlight-img-input").click()}
                    className="w-full h-48 border-2 border-dashed rounded-md flex flex-col items-center justify-center text-muted-foreground hover:border-praroha-sage/50 transition-colors"
                  >
                    <ImageIcon className="w-8 h-8 mb-2" />
                    <span className="text-sm">Click to upload image</span>
                  </button>
                )}
                <input id="spotlight-img-input" type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-praroha-sage hover:bg-praroha-forest" data-testid="spotlight-save-btn">
                {saving ? "Saving..." : editingPost ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
